import { useState } from 'react';
import PageHero from '@/components/feature/PageHero';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { donationHero, donationImpact, bankAccounts, donationFAQ } from '@/mocks/donation';

const DONATION_FORM_URL = 'https://readdy.ai/api/form/d8jpiij8cfr1ihhjf770';

export default function DonationPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    fetch(DONATION_FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })
      .then(() => setFormSubmitted(true))
      .catch(() => setFormSubmitted(true));
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        title={donationHero.title}
        subtitle={donationHero.subtitle}
        imageUrl={donationHero.imageUrl}
        breadcrumb={{ label: 'Home', href: '/' }}
      />

      {/* Impact */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Your Impact
          </h2>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12">
            Every contribution directly supports our programs. Here is what your donation can do.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {donationImpact.map((item, i) => (
              <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:border-primary-300/50">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-xl text-primary-600 w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                  {item.amount}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bank Transfer Info + Contact Form */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            {/* Bank Transfer */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Bank Transfer
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed mb-8">
                You can transfer your donation directly to our official NGO bank accounts. Please include your name as the transaction reference so we can issue a receipt.
              </p>
              <div className="space-y-4">
                {bankAccounts.map((acc, i) => (
                  <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-bank-line text-lg text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <span className="font-heading font-semibold text-foreground-950">{acc.bank}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-foreground-500">Account Name: </span>
                        <span className="text-foreground-900 font-medium">{acc.accountName}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">Account Number: </span>
                        <span className="text-foreground-900 font-medium font-mono tracking-wide">{acc.accountNumber}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-foreground-500 text-xs mt-4 leading-relaxed">
                After making a transfer, please email us at insanperwirapenang@gmail.com with your transaction details so we can send you an official donation receipt for tax purposes.
              </p>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Get in Touch
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed mb-8">
                Interested in donating, volunteering, or learning more? Fill out the form below and our team will get back to you.
              </p>

              {formSubmitted ? (
                <div className="bg-primary-100 border border-primary-200/70 rounded-lg p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-background-50 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-foreground-950 mb-2">Thank You!</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Your message has been received. Our team will get back to you within 2-3 business days.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form=""
                  method="POST"
                  action={DONATION_FORM_URL}
                  onSubmit={handleSubmit}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6"
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="+60"
                      />
                    </div>
                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        I am interested in
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                      >
                        <option value="">Select an option</option>
                        <option value="donation">Making a Donation</option>
                        <option value="volunteer">Volunteering</option>
                        <option value="partnership">Partnership Opportunities</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                        placeholder="Tell us how you would like to get involved..."
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">Maximum 500 characters</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200/70">
        <div className="px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {donationFAQ.map((faq, i) => (
              <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="font-medium text-foreground-950 text-sm md:text-base pr-4">{faq.question}</span>
                  <i className={`text-lg text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}